import React, { useState, useEffect } from 'react'
import { 
  Gift, Clock, Check, X, AlertCircle, Search, Filter,
  TrendingUp, TrendingDown, DollarSign, Zap, User,
  Calendar, ChevronDown, MessageSquare
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import { format, parseISO, formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'

interface Redemption {
  id: string
  user_id: string
  user?: {
    username: string
    display_name: string
  }
  reward_id: string
  reward: {
    id: string
    name: string
    description: string
    cost_points: number
    category: string
    icon: string
    requires_parent_approval: boolean
  }
  points_spent: number
  status: 'pending' | 'approved' | 'rejected' | 'completed'
  notes?: string
  parent_notes?: string
  created_at: string
  processed_at?: string
  processed_by?: string
}

interface Stats {
  pending_count: number
  approved_today: number
  rejected_today: number
  total_points_pending: number
  average_wait_time: number
}

interface ChildStats {
  child_id: string
  display_name: string
  total_redemptions: number
  total_points_spent: number
  pending_count: number
  approved_count: number
  rejected_count: number
  favorite_category: string
  last_redemption: string | null
}

export const RedemptionQueue: React.FC = () => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([])
  const [filteredRedemptions, setFilteredRedemptions] = useState<Redemption[]>([])
  const [stats, setStats] = useState<Stats>({
    pending_count: 0,
    approved_today: 0,
    rejected_today: 0,
    total_points_pending: 0,
    average_wait_time: 0
  })
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending')
  const [selectedChild, setSelectedChild] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [selectedRedemption, setSelectedRedemption] = useState<Redemption | null>(null)
  const [parentNotes, setParentNotes] = useState('')
  const [children, setChildren] = useState<any[]>([])
  const [usingMockData, setUsingMockData] = useState(false)
  const [childStats, setChildStats] = useState<ChildStats[]>([])
  const [showPerKidStats, setShowPerKidStats] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    filterRedemptions()
  }, [redemptions, filter, selectedChild, searchTerm])

  const loadData = async () => {
    setLoading(true)
    try {
      // Try to load real data first
      const [redemptionsData, childrenData] = await Promise.all([
        apiClient.get('/api/rewards/redemptions').catch(() => null),
        apiClient.get('/api/parent/children').catch(() => null)
      ])

      // Defensive check: Ensure redemptionsData is an array
      const redemptionsArray = redemptionsData && (Array.isArray(redemptionsData) ? redemptionsData :
                                (redemptionsData?.redemptions && Array.isArray(redemptionsData.redemptions)) ? redemptionsData.redemptions : [])

      if (redemptionsData) {
        setRedemptions(redemptionsArray) // Even if empty array
        setUsingMockData(false)
      } else {
        // Only use empty array if API call failed
        setRedemptions([])
        setUsingMockData(false)
      }

      // Defensive check: Ensure childrenData is an array
      const childrenArray = childrenData && (Array.isArray(childrenData) ? childrenData :
                            (childrenData?.children && Array.isArray(childrenData.children)) ? childrenData.children : null)

      if (childrenArray) {
        setChildren(childrenArray)
      } else {
        setChildren([])
      }

      // Calculate stats
      const today = new Date().toDateString()
      const pending = redemptionsArray.filter((r: Redemption) => r.status === 'pending')
      const approvedToday = redemptionsArray.filter((r: Redemption) =>
        r.status === 'approved' &&
        r.processed_at &&
        new Date(r.processed_at).toDateString() === today
      )
      const rejectedToday = redemptionsArray.filter((r: Redemption) => 
        r.status === 'rejected' && 
        r.processed_at && 
        new Date(r.processed_at).toDateString() === today
      )

      const totalPending = pending.reduce((sum: number, r: Redemption) => 
        sum + r.points_spent, 0
      )

      // Calculate average wait time for pending items
      const waitTimes = pending.map((r: Redemption) => 
        Date.now() - new Date(r.created_at).getTime()
      )
      const avgWait = waitTimes.length > 0 
        ? waitTimes.reduce((a, b) => a + b, 0) / waitTimes.length / (1000 * 60 * 60) // in hours
        : 0

      setStats({
        pending_count: pending.length,
        approved_today: approvedToday.length,
        rejected_today: rejectedToday.length,
        total_points_pending: totalPending,
        average_wait_time: Math.round(avgWait)
      })

      // Calculate per-child stats
      const childStatsMap = new Map<string, ChildStats>()

      children.forEach(child => {
        const childRedemptions = redemptionsArray.filter((r: Redemption) => r.user_id === child.id)

        // Count by status
        const childPending = childRedemptions.filter((r: Redemption) => r.status === 'pending')
        const childApproved = childRedemptions.filter((r: Redemption) => r.status === 'approved')
        const childRejected = childRedemptions.filter((r: Redemption) => r.status === 'rejected')

        // Calculate total points spent
        const totalPoints = childRedemptions.reduce((sum: number, r: Redemption) =>
          sum + r.points_spent, 0
        )

        // Find favorite category (most redeemed category)
        const categoryCounts = childRedemptions.reduce((acc: Record<string, number>, r: Redemption) => {
          const cat = r.reward.category
          acc[cat] = (acc[cat] || 0) + 1
          return acc
        }, {})

        const favoriteCategory = Object.entries(categoryCounts).sort(([, a], [, b]) => b - a)[0]?.[0] || 'none'

        // Get last redemption date
        const lastRedemption = childRedemptions.length > 0
          ? childRedemptions.sort((a, b) =>
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0].created_at
          : null

        childStatsMap.set(child.id, {
          child_id: child.id,
          display_name: child.display_name,
          total_redemptions: childRedemptions.length,
          total_points_spent: totalPoints,
          pending_count: childPending.length,
          approved_count: childApproved.length,
          rejected_count: childRejected.length,
          favorite_category: favoriteCategory,
          last_redemption: lastRedemption
        })
      })

      setChildStats(Array.from(childStatsMap.values()).sort((a, b) =>
        b.total_redemptions - a.total_redemptions
      ))
    } catch (error) {
      console.error('Failed to load redemptions:', error)
      toast.error('Failed to load redemption queue')
    } finally {
      setLoading(false)
    }
  }

  const filterRedemptions = () => {
    let filtered = [...redemptions]

    // Status filter
    if (filter !== 'all') {
      filtered = filtered.filter(r => r.status === filter)
    }

    // Child filter
    if (selectedChild !== 'all') {
      filtered = filtered.filter(r => r.user_id === selectedChild)
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(r => 
        r.reward.name.toLowerCase().includes(search) ||
        r.reward.description?.toLowerCase().includes(search) ||
        r.user?.display_name?.toLowerCase().includes(search)
      )
    }

    // Sort by created date (newest first for pending, oldest first for processed)
    filtered.sort((a, b) => {
      if (a.status === 'pending' && b.status === 'pending') {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime() // Oldest pending first
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime() // Newest processed first
    })

    setFilteredRedemptions(filtered)
  }

  const handleApprove = async (redemption: Redemption) => {
    setProcessingId(redemption.id)
    try {
      await apiClient.patch(`/api/rewards/redemptions/${redemption.id}/approve`, {
        parent_notes: parentNotes
      })
      toast.success(`Approved ${redemption.reward.name} for ${redemption.user?.display_name}`)
      loadData()
      setShowNotesModal(false)
      setParentNotes('')
      setSelectedRedemption(null)
    } catch (error) {
      console.error('Failed to approve redemption:', error)
      toast.error('Failed to approve redemption')
    } finally {
      setProcessingId(null)
    }
  }

  const handleReject = async (redemption: Redemption) => {
    if (!parentNotes.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }

    setProcessingId(redemption.id)
    try {
      await apiClient.patch(`/api/rewards/redemptions/${redemption.id}/reject`, {
        parent_notes: parentNotes
      })
      toast.success(`Rejected ${redemption.reward.name} for ${redemption.user?.display_name}`)
      loadData()
      setShowNotesModal(false)
      setParentNotes('')
      setSelectedRedemption(null)
    } catch (error) {
      console.error('Failed to reject redemption:', error)
      toast.error('Failed to reject redemption')
    } finally {
      setProcessingId(null)
    }
  }

  const openNotesModal = (redemption: Redemption, action: 'approve' | 'reject') => {
    setSelectedRedemption(redemption)
    setShowNotesModal(true)
    if (action === 'reject') {
      setParentNotes('') // Clear for rejection reason
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'completed':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'money':
        return <DollarSign className="w-4 h-4" />
      case 'privilege':
        return <TrendingUp className="w-4 h-4" />
      case 'item':
        return <Gift className="w-4 h-4" />
      default:
        return <Zap className="w-4 h-4" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Empty State */}
      {redemptions.length === 0 && !loading && (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <Gift className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Redemptions Yet</h3>
          <p className="text-gray-600 mb-4">
            Kids haven't redeemed any rewards yet. Once they start redeeming, you'll see them here for approval.
          </p>
          <p className="text-sm text-gray-500">
            Encourage kids to earn points and check out the rewards catalog!
          </p>
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-900">{stats.pending_count}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
          <p className="text-xs text-yellow-600 mt-2">
            {stats.total_points_pending} points waiting
          </p>
        </div>

        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Approved Today</p>
              <p className="text-2xl font-bold text-green-900">{stats.approved_today}</p>
            </div>
            <Check className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600">Rejected Today</p>
              <p className="text-2xl font-bold text-red-900">{stats.rejected_today}</p>
            </div>
            <X className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600">Avg Wait Time</p>
              <p className="text-2xl font-bold text-blue-900">{stats.average_wait_time}h</p>
            </div>
            <Clock className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600">Total Processed</p>
              <p className="text-2xl font-bold text-purple-900">
                {redemptions.filter(r => r.status !== 'pending').length}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Per-Kid Stats */}
      {childStats.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Per-Child Redemption Stats</h3>
            <button
              onClick={() => setShowPerKidStats(!showPerKidStats)}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
            >
              {showPerKidStats ? (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Hide
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1 transform rotate-180" />
                  Show
                </>
              )}
            </button>
          </div>

          {showPerKidStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {childStats.map(childStat => (
                <div
                  key={childStat.child_id}
                  className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <User className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-gray-900">{childStat.display_name}</h4>
                    </div>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      {childStat.favorite_category}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Total Redemptions</span>
                      <span className="font-bold text-gray-900">{childStat.total_redemptions}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Points Spent</span>
                      <span className="font-bold text-yellow-600 flex items-center">
                        <Zap className="w-3 h-3 mr-1" />
                        {childStat.total_points_spent}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs pt-2 border-t border-blue-200">
                      <div className="flex items-center space-x-1">
                        <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                        <span className="text-gray-600">Pending: {childStat.pending_count}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                        <span className="text-gray-600">{childStat.approved_count}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                        <span className="text-gray-600">{childStat.rejected_count}</span>
                      </div>
                    </div>
                    {childStat.last_redemption && (
                      <div className="text-xs text-gray-500 pt-1">
                        Last: {formatDistanceToNow(parseISO(childStat.last_redemption), { addSuffix: true })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search rewards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <select
            value={selectedChild}
            onChange={(e) => setSelectedChild(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">All Children</option>
            {children.map(child => (
              <option key={child.id} value={child.id}>
                {child.display_name}
              </option>
            ))}
          </select>

          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {(['all', 'pending', 'approved', 'rejected'] as const).map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Redemption List */}
      <div className="space-y-3">
        {filteredRedemptions.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Gift className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No redemptions found</h3>
            <p className="text-gray-600">
              {filter === 'pending' ? 'No pending redemptions to review' : 'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          filteredRedemptions.map(redemption => (
            <div key={redemption.id} className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-start space-x-3">
                    <div className="text-2xl">{redemption.reward.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900">
                          {redemption.reward.name}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(redemption.status)}`}>
                          {redemption.status}
                        </span>
                        <div className="flex items-center space-x-1 text-sm text-gray-500">
                          {getCategoryIcon(redemption.reward.category)}
                          <span>{redemption.reward.category}</span>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">
                        {redemption.reward.description}
                      </p>
                      
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{redemption.user?.display_name}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Zap className="w-4 h-4 text-yellow-500" />
                          <span className="font-medium">{redemption.points_spent} points</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>{formatDistanceToNow(parseISO(redemption.created_at), { addSuffix: true })}</span>
                        </div>
                      </div>

                      {redemption.notes && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                          <span className="font-medium">Child's note: </span>
                          {redemption.notes}
                        </div>
                      )}

                      {redemption.parent_notes && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                          <span className="font-medium">Parent's note: </span>
                          {redemption.parent_notes}
                        </div>
                      )}

                      {redemption.processed_at && (
                        <div className="mt-2 text-xs text-gray-500">
                          Processed {format(parseISO(redemption.processed_at), 'MMM d, yyyy h:mm a')}
                          {redemption.processed_by && ` by ${redemption.processed_by}`}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {redemption.status === 'pending' && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openNotesModal(redemption, 'approve')}
                      disabled={processingId === redemption.id}
                      className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 flex items-center space-x-1"
                    >
                      <Check className="w-4 h-4" />
                      <span>Approve</span>
                    </button>
                    <button
                      onClick={() => openNotesModal(redemption, 'reject')}
                      disabled={processingId === redemption.id}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 flex items-center space-x-1"
                    >
                      <X className="w-4 h-4" />
                      <span>Reject</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Notes Modal */}
      {showNotesModal && selectedRedemption && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              Review Redemption Request
            </h3>
            
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-2xl">{selectedRedemption.reward.icon}</span>
                <div>
                  <h4 className="font-medium">{selectedRedemption.reward.name}</h4>
                  <p className="text-sm text-gray-600">
                    {selectedRedemption.user?.display_name} • {selectedRedemption.points_spent} points
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Add a note (optional for approval, required for rejection)
              </label>
              <textarea
                value={parentNotes}
                onChange={(e) => setParentNotes(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows={3}
                placeholder="E.g., 'Great job!' or 'Let's talk about this first...'"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNotesModal(false)
                  setParentNotes('')
                  setSelectedRedemption(null)
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReject(selectedRedemption)}
                disabled={!parentNotes.trim()}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
              >
                Reject
              </button>
              <button
                onClick={() => handleApprove(selectedRedemption)}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RedemptionQueue